import { RequireAccess } from '@/components/require-access'
import { useJobillaOrgId } from '@/hooks'
import { logger, SettingsHeading, SettingsNav } from '@/ui'
import { emitUnhandledError, SwitchField } from '@/ui/rapid-ui'
import { SettingsSubheading } from '@/ui/views/settings/shared/settings-typography'
import {
	organisationAiSettingsShowQueryKey,
	roleEnum,
	useOrganisationAiSettingsShow,
	useOrganisationAiSettingsUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CheckIcon, LockIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const PII_CAPABILITIES = [
	{ key: 'talentPoolSearch', product: 'jobibot' },
	{ key: 'candidateSuggestions', product: 'jobibot' },
	{ key: 'personConversation', product: 'jobibot' },
	{ key: 'whatsappAgent', product: 'whatsappAgent' },
] as const

export const Route = createFileRoute('/_auth/company/$companyId/settings/ai')({
	component: RouteComponent,
})

function RouteComponent() {
	const { t } = useTranslation('common')

	return (
		<SettingsNav active="ai-settings">
			<SettingsHeading>{t('settings.aiSettings.title')}</SettingsHeading>
			<RequireAccess
				role={[roleEnum.admin, roleEnum.jobillan]}
				fallback={<p className="text-red-600">{t('settings.aiSettings.noPermission')}</p>}
			>
				<AiSettingsContent />
			</RequireAccess>
		</SettingsNav>
	)
}

function AiSettingsContent() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()

	const { data: aiSettings, isPending } = useOrganisationAiSettingsShow(jobillaOrgId, {
		enabled: !!jobillaOrgId,
	})

	const { mutateAsync: updateAiSettings, isPending: isSaving } = useOrganisationAiSettingsUpdate()

	const [justSaved, setJustSaved] = useState(false)
	const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const aiPiiEnabled = aiSettings?.aiPiiEnabled ?? false

	const handleToggle = async () => {
		if (!jobillaOrgId || isSaving) { return }
		try {
			await updateAiSettings({
				organisation: jobillaOrgId,
				data: { aiPiiEnabled: !aiPiiEnabled },
			})
			await queryClient.invalidateQueries({
				queryKey: organisationAiSettingsShowQueryKey(jobillaOrgId),
			})
			setJustSaved(true)
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
			savedTimerRef.current = setTimeout(() => setJustSaved(false), 2000)
		} catch (err) {
			logger.error(err, { tags: { component: 'AiSettings', action: 'toggle-pii' } })
			emitUnhandledError({
				title: t('settings.aiSettings.personalData.saveError'),
				description: err instanceof Error ? err.message : undefined,
				cause: err,
			})
		}
	}

	return (
		<div className="space-y-12 max-w-[680px]">
			<section>
				<SettingsSubheading>{t('settings.aiSettings.personalData.heading')}</SettingsSubheading>
				<div className="mt-4">
					<SwitchField
						checked={aiPiiEnabled}
						onChange={() => void handleToggle()}
						disabled={isSaving}
						isLoading={isPending}
						label={t('settings.aiSettings.personalData.toggleLabel')}
						description={t('settings.aiSettings.personalData.description')}
						labelAddon={
							<SavedBadge visible={justSaved} label={t('settings.aiSettings.personalData.saved')} />
						}
					/>
				</div>
			</section>

			<section>
				<h3 className="text-base font-semibold text-grey-900">
					{t('settings.aiSettings.capabilities.heading')}
				</h3>
				<p className="mt-1 text-sm text-grey-500">
					{t('settings.aiSettings.capabilities.description')}
				</p>

				<ul className="mt-4 space-y-3">
					{PII_CAPABILITIES.map(({ key, product }) => (
						<li
							key={key}
							className={`flex items-start gap-3 rounded-lg border p-4 transition-colors
								${aiPiiEnabled ? 'border-grey-200 bg-white' : 'border-grey-100 bg-grey-50'}`}
						>
							<span
								className={`mt-0.5 flex-shrink-0 ${aiPiiEnabled ? 'text-green-500' : 'text-grey-300'}`}
							>
								{aiPiiEnabled
									? <CheckIcon className="size-4" strokeWidth={2.5} />
									: <LockIcon className="size-4" />}
							</span>
							<div>
								<p
									className={`text-xs font-semibold uppercase tracking-wide ${
										aiPiiEnabled ? 'text-grey-500' : 'text-grey-400'
									}`}
								>
									{t(`settings.aiSettings.capabilities.products.${product}`)}
								</p>
								<p
									className={`mt-0.5 text-sm font-medium ${
										aiPiiEnabled ? 'text-grey-900' : 'text-grey-400'
									}`}
								>
									{t(`settings.aiSettings.capabilities.${key}.title`)}
								</p>
								<p className={`mt-0.5 text-xs ${aiPiiEnabled ? 'text-grey-500' : 'text-grey-400'}`}>
									{t('settings.aiSettings.capabilities.usesPrefix')}{' '}
									{t(`settings.aiSettings.capabilities.${key}.uses`)}
								</p>
							</div>
						</li>
					))}
				</ul>
			</section>
		</div>
	)
}

function SavedBadge({ visible, label }: { visible: boolean; label: string }) {
	return (
		<AnimatePresence>
			{visible
				? (
					<motion.span
						key="saved"
						initial={{ opacity: 0, scale: 0.85 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.85 }}
						transition={{ duration: 0.15 }}
						className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200"
					>
						<CheckIcon className="size-3" strokeWidth={2.5} />
						{label}
					</motion.span>
				)
				: null}
		</AnimatePresence>
	)
}
